/**
 * Technical Support Decision Tree Logic
 * Handles the conversational flow for kiosk technical support
 * Multi-language support included
 */

// Language detection helper
export const getCurrentLanguage = () => {
  // Try to get language from Google Translate cookie
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'googtrans') {
      const lang = value.split('/')[2];
      return lang || 'es';
    }
  }
  // Fallback to browser language or Spanish
  const browserLang = navigator.language?.split('-')[0] || 'es';
  return browserLang;
};

export const SUPPORT_CATEGORIES = {
  SCREEN: 'screen',
  SCREEN_OFF: 'screen_off',
  PAYMENT: 'payment',
  OTHER: 'other'
};

export const ISSUE_TYPES = {
  TOUCH_NOT_WORKING: 'touch_not_working',
  SCREEN_OFF: 'screen_off',
  PAYMENT_DEVICE_ISSUE: 'payment_device_issue',
  OTHER: 'other_issue'
};

/**
 * Decision tree node structure
 */
class DecisionNode {
  constructor(id, messageKey, options = [], isEndNode = false, escalate = false) {
    this.id = id;
    this.messageKey = messageKey; // Key for translation
    this.options = options; // Array of {textKey, nextNode, userResponse}
    this.isEndNode = isEndNode;
    this.escalate = escalate; // If true, create incident ticket
  }

  getMessage(lang = 'es') {
    return translations[lang]?.[this.messageKey] || translations['es'][this.messageKey] || this.messageKey;
  }

  getOptions(lang = 'es') {
    return this.options.map(opt => ({
      ...opt,
      text: translations[lang]?.[opt.textKey] || translations['es'][opt.textKey] || opt.textKey,
      nextNode: opt.nextNode,
      requiresInput: opt.requiresInput
    }));
  }
}

/**
 * Translations for all supported languages
 */
const translations = {
  es: {
    // Start
    'msg.start': '¡Hola! Soy el asistente técnico de VACWAY. Estoy aquí para ayudarte con problemas técnicos del kiosco.\n\n¿Qué tipo de problema estás experimentando?',
    'opt.touch_not_working': 'Pantalla táctil no funciona',
    'opt.screen_off': 'Pantalla apagada',
    'opt.payment_issue': 'Problema con método de pago',
    'opt.other_issue': 'Otro problema',
    
    // Touch screen flow
    'msg.touch_not_working': 'Entiendo que el táctil de la pantalla no está funcionando.\n\nPor favor, realiza el siguiente paso:\n1. Desconecta completamente la máquina de la corriente\n2. Espera 10 segundos\n3. Vuelve a conectar la máquina\n\n¿Funciona el táctil después de reiniciar?',
    'opt.yes_works': 'Sí, el táctil funciona ahora',
    'opt.no_not_working': 'No, el táctil sigue sin funcionar',
    'msg.touch_resolved': '¡Excelente! Me alegro de que el problema se haya resuelto.\n\nEl reinicio completo del sistema ha solucionado el problema del táctil. Si vuelves a experimentar este problema, no dudes en contactarnos nuevamente.\n\n¿Hay algo más en lo que pueda ayudarte?',
    'msg.touch_escalate': 'Lamento que el problema persista. Voy a crear una incidencia para que un técnico especializado se ponga en contacto contigo a la mayor brevedad posible.\n\nPor favor, proporciona tu información de contacto y cualquier detalle adicional que consideres relevante.',
    
    // Screen off flow
    'msg.screen_off': 'Entiendo que la pantalla de la máquina está apagada.\n\nPrimero, verifiquemos el suministro eléctrico:\n¿El TPV (terminal de pago) o el billetero están encendidos?',
    'opt.no_payment_off': 'No, los métodos de pago están apagados',
    'opt.yes_payment_on': 'Sí, los métodos de pago están encendidos',
    'opt.dont_know': 'No lo sé / No puedo verificar',
    'msg.power_check': 'Parece que la máquina no está recibiendo corriente eléctrica.\n\nPor favor:\n1. Verifica que el cable de alimentación esté correctamente conectado\n2. Comprueba que el enchufe tiene corriente\n3. Conecta la máquina a la corriente\n\n¿Enciende la máquina ahora?',
    'opt.no_not_turn_on': 'No, la máquina no enciende',
    'opt.yes_works_now': 'Sí, la máquina enciende correctamente',
    'msg.power_escalate': 'Hay un problema con el sistema eléctrico de la máquina que requiere atención especializada.\n\nVoy a crear una incidencia urgente para que un técnico se ponga en contacto contigo lo antes posible.',
    'msg.power_resolved': '¡Perfecto! La máquina está funcionando correctamente ahora.\n\nEl problema era la conexión eléctrica. Asegúrate de que el cable de alimentación esté bien asegurado para evitar desconexiones accidentales.\n\n¿Necesitas ayuda con algo más?',
    'msg.erp_check': 'Voy a revisar el estado de la máquina en nuestro sistema ERP.\n\nPor favor, proporciona el ID o número de serie de la máquina para verificar su estado (online/offline).',
    'opt.enter_machine_id': 'Ingresar ID de máquina',
    'msg.erp_status_check': 'Revisando estado en ERP...\n\n{ERP_STATUS_MESSAGE}\n\nPor favor, realiza lo siguiente:\n1. Desconecta la máquina de la corriente\n2. Espera 10 segundos\n3. Vuelve a conectar\n4. Observa la pantalla azul de inicialización\n\n¿Aparece algún mensaje en la pantalla azul al iniciar?',
    'opt.no_message': 'No, no aparece mensaje en la pantalla azul',
    'opt.yes_message': 'Sí, aparece mensaje en la pantalla azul',
    'opt.works_now': 'La máquina funciona correctamente ahora',
    'msg.screen_hardware_escalate': 'El problema parece ser de hardware de la pantalla. Requiere revisión técnica especializada.\n\nVoy a crear una incidencia para que un técnico se ponga en contacto contigo.',
    'msg.screen_software_escalate': 'El problema parece estar relacionado con el software del sistema. Requiere diagnóstico especializado.\n\nVoy a crear una incidencia para que un técnico se ponga en contacto contigo.',
    'msg.screen_resolved': '¡Excelente! La máquina está funcionando correctamente.\n\nEl reinicio del sistema ha resuelto el problema. ¿Hay algo más en lo que pueda ayudarte?',
    
    // Payment issues
    'msg.payment_issue': '¿Qué tipo de problema estás experimentando con el método de pago?',
    'opt.tpv_issue': 'TPV no funciona',
    'opt.bill_acceptor': 'Billetero no funciona',
    'opt.card_issue': 'No acepta tarjetas',
    'opt.bill_issue': 'No acepta billetes',
    'msg.tpv_issue': 'Problema con el TPV detectado. Este tipo de incidencia requiere atención especializada.\n\nVoy a crear un ticket para que un técnico revise el terminal de pago.',
    'msg.bill_acceptor_issue': 'Problema con el billetero detectado. Este tipo de incidencia requiere atención especializada.\n\nVoy a crear un ticket para que un técnico revise el aceptador de billetes.',
    'msg.card_issue': 'Problema con lector de tarjetas detectado. Este tipo de incidencia requiere atención especializada.\n\nVoy a crear un ticket para que un técnico revise el sistema de pago con tarjeta.',
    'msg.bill_issue': 'Problema con aceptación de billetes detectado. Este tipo de incidencia requiere atención especializada.\n\nVoy a crear un ticket para que un técnico revise el billetero.',
    
    // Other issues
    'msg.other_issue': 'Por favor, describe el problema que estás experimentando con el mayor detalle posible. Incluye:\n- ¿Qué componente o funcionalidad no está funcionando?\n- ¿Cuándo comenzó el problema?\n- ¿Has notado algún mensaje de error?\n\nUn técnico especializado revisará tu caso.',
    'opt.send_description': 'Enviar descripción y crear incidencia',
    'msg.other_escalate': 'He registrado tu caso. Un técnico especializado se pondrá en contacto contigo para resolver este problema.\n\nGracias por proporcionar los detalles.',
    
    // Common options
    'opt.no_thanks': 'No, eso es todo. Gracias',
    'opt.yes_other': 'Sí, tengo otro problema',
    
    // End
    'msg.end': 'Gracias por contactar con el soporte técnico de VACWAY. Si necesitas ayuda en el futuro, no dudes en abrir una nueva consulta.\n\n¡Que tengas un buen día!'
  },
  en: {
    'msg.start': 'Hello! I\'m the VACWAY technical assistant. I\'m here to help you with kiosk technical issues.\n\nWhat type of problem are you experiencing?',
    'opt.touch_not_working': 'Touch screen not working',
    'opt.screen_off': 'Screen is off',
    'opt.payment_issue': 'Payment method issue',
    'opt.other_issue': 'Other issue',
    'msg.touch_not_working': 'I understand the touch screen is not working.\n\nPlease follow this step:\n1. Completely disconnect the machine from power\n2. Wait 10 seconds\n3. Reconnect the machine\n\nDoes the touch screen work after restarting?',
    'opt.yes_works': 'Yes, the touch screen works now',
    'opt.no_not_working': 'No, the touch screen still doesn\'t work',
    'msg.touch_resolved': 'Excellent! I\'m glad the problem has been resolved.\n\nThe complete system restart has fixed the touch screen issue. If you experience this problem again, don\'t hesitate to contact us.\n\nIs there anything else I can help you with?',
    'msg.touch_escalate': 'I\'m sorry the problem persists. I\'ll create an incident so a specialized technician will contact you as soon as possible.\n\nPlease provide your contact information and any additional details you consider relevant.',
    'msg.screen_off': 'I understand the machine screen is off.\n\nFirst, let\'s check the power supply:\nIs the POS (payment terminal) or bill acceptor turned on?',
    'opt.no_payment_off': 'No, payment methods are off',
    'opt.yes_payment_on': 'Yes, payment methods are on',
    'opt.dont_know': 'I don\'t know / Can\'t verify',
    'msg.power_check': 'It seems the machine is not receiving electrical power.\n\nPlease:\n1. Verify the power cable is properly connected\n2. Check the outlet has power\n3. Connect the machine to power\n\nDoes the machine turn on now?',
    'opt.no_not_turn_on': 'No, the machine doesn\'t turn on',
    'opt.yes_works_now': 'Yes, the machine turns on correctly',
    'msg.power_escalate': 'There\'s an issue with the machine\'s electrical system that requires specialized attention.\n\nI\'ll create an urgent incident so a technician contacts you as soon as possible.',
    'msg.power_resolved': 'Perfect! The machine is working correctly now.\n\nThe problem was the electrical connection. Make sure the power cable is properly secured to avoid accidental disconnections.\n\nDo you need help with anything else?',
    'msg.erp_check': 'I\'ll check the machine status in our ERP system.\n\nPlease provide the machine ID or serial number to verify its status (online/offline).',
    'opt.enter_machine_id': 'Enter machine ID',
    'msg.erp_status_check': 'Checking ERP status...\n\n{ERP_STATUS_MESSAGE}\n\nPlease do the following:\n1. Disconnect the machine from power\n2. Wait 10 seconds\n3. Reconnect\n4. Watch the blue initialization screen\n\nDoes any message appear on the blue screen during startup?',
    'opt.no_message': 'No, no message appears on the blue screen',
    'opt.yes_message': 'Yes, a message appears on the blue screen',
    'opt.works_now': 'The machine works correctly now',
    'msg.screen_hardware_escalate': 'The problem appears to be screen hardware. It requires specialized technical review.\n\nI\'ll create an incident for a technician to contact you.',
    'msg.screen_software_escalate': 'The problem seems to be related to system software. It requires specialized diagnosis.\n\nI\'ll create an incident for a technician to contact you.',
    'msg.screen_resolved': 'Excellent! The machine is working correctly.\n\nThe system restart has resolved the problem. Is there anything else I can help you with?',
    'msg.payment_issue': 'What type of payment method problem are you experiencing?',
    'opt.tpv_issue': 'POS not working',
    'opt.bill_acceptor': 'Bill acceptor not working',
    'opt.card_issue': 'Not accepting cards',
    'opt.bill_issue': 'Not accepting bills',
    'msg.tpv_issue': 'POS problem detected. This type of incident requires specialized attention.\n\nI\'ll create a ticket for a technician to review the payment terminal.',
    'msg.bill_acceptor_issue': 'Bill acceptor problem detected. This type of incident requires specialized attention.\n\nI\'ll create a ticket for a technician to review the bill acceptor.',
    'msg.card_issue': 'Card reader problem detected. This type of incident requires specialized attention.\n\nI\'ll create a ticket for a technician to review the card payment system.',
    'msg.bill_issue': 'Bill acceptance problem detected. This type of incident requires specialized attention.\n\nI\'ll create a ticket for a technician to review the bill acceptor.',
    'msg.other_issue': 'Please describe the problem you\'re experiencing in as much detail as possible. Include:\n- Which component or functionality is not working?\n- When did the problem start?\n- Have you noticed any error messages?\n\nA specialized technician will review your case.',
    'opt.send_description': 'Send description and create incident',
    'msg.other_escalate': 'I\'ve registered your case. A specialized technician will contact you to resolve this problem.\n\nThank you for providing the details.',
    'opt.no_thanks': 'No, that\'s all. Thank you',
    'opt.yes_other': 'Yes, I have another problem',
    'msg.end': 'Thank you for contacting VACWAY technical support. If you need help in the future, don\'t hesitate to open a new inquiry.\n\nHave a great day!'
  },
  fr: {
    'msg.start': 'Bonjour ! Je suis l\'assistant technique VACWAY. Je suis là pour vous aider avec les problèmes techniques du kiosque.\n\nQuel type de problème rencontrez-vous ?',
    'opt.touch_not_working': 'L\'écran tactile ne fonctionne pas',
    'opt.screen_off': 'L\'écran est éteint',
    'opt.payment_issue': 'Problème de méthode de paiement',
    'opt.other_issue': 'Autre problème',
    'msg.touch_not_working': 'Je comprends que l\'écran tactile ne fonctionne pas.\n\nVeuillez suivre cette étape :\n1. Débranchez complètement la machine de l\'alimentation\n2. Attendez 10 secondes\n3. Rebranchez la machine\n\nL\'écran tactile fonctionne-t-il après le redémarrage ?',
    'opt.yes_works': 'Oui, l\'écran tactile fonctionne maintenant',
    'opt.no_not_working': 'Non, l\'écran tactile ne fonctionne toujours pas',
    'msg.touch_resolved': 'Excellent ! Je suis content que le problème ait été résolu.\n\nLe redémarrage complet du système a résolu le problème de l\'écran tactile. Si vous rencontrez à nouveau ce problème, n\'hésitez pas à nous contacter.\n\nY a-t-il autre chose pour laquelle je peux vous aider ?',
    'msg.touch_escalate': 'Je suis désolé que le problème persiste. Je vais créer un incident pour qu\'un technicien spécialisé vous contacte dès que possible.\n\nVeuillez fournir vos coordonnées et tout détail supplémentaire que vous jugez pertinent.',
    'msg.screen_off': 'Je comprends que l\'écran de la machine est éteint.\n\nTout d\'abord, vérifions l\'alimentation électrique :\nLe TPE (terminal de paiement) ou l\'accepteur de billets est-il allumé ?',
    'opt.no_payment_off': 'Non, les méthodes de paiement sont éteintes',
    'opt.yes_payment_on': 'Oui, les méthodes de paiement sont allumées',
    'opt.dont_know': 'Je ne sais pas / Je ne peux pas vérifier',
    'msg.power_check': 'Il semble que la machine ne reçoit pas d\'alimentation électrique.\n\nVeuillez :\n1. Vérifier que le câble d\'alimentation est correctement connecté\n2. Vérifier que la prise a du courant\n3. Connecter la machine à l\'alimentation\n\nLa machine s\'allume-t-elle maintenant ?',
    'opt.no_not_turn_on': 'Non, la machine ne s\'allume pas',
    'opt.yes_works_now': 'Oui, la machine s\'allume correctement',
    'msg.power_escalate': 'Il y a un problème avec le système électrique de la machine qui nécessite une attention spécialisée.\n\nJe vais créer un incident urgent pour qu\'un technicien vous contacte dès que possible.',
    'msg.power_resolved': 'Parfait ! La machine fonctionne correctement maintenant.\n\nLe problème était la connexion électrique. Assurez-vous que le câble d\'alimentation est bien fixé pour éviter les déconnexions accidentelles.\n\nAvez-vous besoin d\'aide pour autre chose ?',
    'msg.erp_check': 'Je vais vérifier l\'état de la machine dans notre système ERP.\n\nVeuillez fournir l\'ID ou le numéro de série de la machine pour vérifier son état (en ligne/hors ligne).',
    'opt.enter_machine_id': 'Entrer l\'ID de la machine',
    'msg.erp_status_check': 'Vérification de l\'état ERP...\n\n{ERP_STATUS_MESSAGE}\n\nVeuillez faire ce qui suit :\n1. Débranchez la machine de l\'alimentation\n2. Attendez 10 secondes\n3. Rebranchez\n4. Observez l\'écran bleu d\'initialisation\n\nUn message apparaît-il sur l\'écran bleu au démarrage ?',
    'opt.no_message': 'Non, aucun message n\'apparaît sur l\'écran bleu',
    'opt.yes_message': 'Oui, un message apparaît sur l\'écran bleu',
    'opt.works_now': 'La machine fonctionne correctement maintenant',
    'msg.screen_hardware_escalate': 'Le problème semble être matériel au niveau de l\'écran. Il nécessite un examen technique spécialisé.\n\nJe vais créer un incident pour qu\'un technicien vous contacte.',
    'msg.screen_software_escalate': 'Le problème semble être lié au logiciel du système. Il nécessite un diagnostic spécialisé.\n\nJe vais créer un incident pour qu\'un technicien vous contacte.',
    'msg.screen_resolved': 'Excellent ! La machine fonctionne correctement.\n\nLe redémarrage du système a résolu le problème. Y a-t-il autre chose pour laquelle je peux vous aider ?',
    'msg.payment_issue': 'Quel type de problème de paiement rencontrez-vous ?',
    'opt.tpv_issue': 'Le TPE ne fonctionne pas',
    'opt.bill_acceptor': 'L\'accepteur de billets ne fonctionne pas',
    'opt.card_issue': 'N\'accepte pas les cartes',
    'opt.bill_issue': 'N\'accepte pas les billets',
    'msg.tpv_issue': 'Problème de TPE détecté. Ce type d\'incident nécessite une attention spécialisée.\n\nJe vais créer un ticket pour qu\'un technicien examine le terminal de paiement.',
    'msg.bill_acceptor_issue': 'Problème d\'accepteur de billets détecté. Ce type d\'incident nécessite une attention spécialisée.\n\nJe vais créer un ticket pour qu\'un technicien examine l\'accepteur de billets.',
    'msg.card_issue': 'Problème de lecteur de carte détecté. Ce type d\'incident nécessite une attention spécialisée.\n\nJe vais créer un ticket pour qu\'un technicien examine le système de paiement par carte.',
    'msg.bill_issue': 'Problème d\'acceptation de billets détecté. Ce type d\'incident nécessite une attention spécialisée.\n\nJe vais créer un ticket pour qu\'un technicien examine l\'accepteur de billets.',
    'msg.other_issue': 'Veuillez décrire le problème que vous rencontrez avec le plus de détails possible. Incluez :\n- Quel composant ou fonctionnalité ne fonctionne pas ?\n- Quand le problème a-t-il commencé ?\n- Avez-vous remarqué des messages d\'erreur ?\n\nUn technicien spécialisé examinera votre cas.',
    'opt.send_description': 'Envoyer la description et créer un incident',
    'msg.other_escalate': 'J\'ai enregistré votre cas. Un technicien spécialisé vous contactera pour résoudre ce problème.\n\nMerci d\'avoir fourni les détails.',
    'opt.no_thanks': 'Non, c\'est tout. Merci',
    'opt.yes_other': 'Oui, j\'ai un autre problème',
    'msg.end': 'Merci d\'avoir contacté le support technique VACWAY. Si vous avez besoin d\'aide à l\'avenir, n\'hésitez pas à ouvrir une nouvelle demande.\n\nBonne journée !'
  },
  de: {
    'msg.start': 'Hallo! Ich bin der technische Assistent von VACWAY. Ich bin hier, um Ihnen bei technischen Problemen mit dem Kiosk zu helfen.\n\nWelche Art von Problem haben Sie?',
    'opt.touch_not_working': 'Touchscreen funktioniert nicht',
    'opt.screen_off': 'Bildschirm ist aus',
    'opt.payment_issue': 'Problem mit Zahlungsmethode',
    'opt.other_issue': 'Anderes Problem',
    'msg.touch_not_working': 'Ich verstehe, dass der Touchscreen nicht funktioniert.\n\nBitte führen Sie diesen Schritt aus:\n1. Trennen Sie das Gerät vollständig von der Stromversorgung\n2. Warten Sie 10 Sekunden\n3. Schließen Sie das Gerät wieder an\n\nFunktioniert der Touchscreen nach dem Neustart?',
    'opt.yes_works': 'Ja, der Touchscreen funktioniert jetzt',
    'opt.no_not_working': 'Nein, der Touchscreen funktioniert immer noch nicht',
    'msg.touch_resolved': 'Ausgezeichnet! Ich freue mich, dass das Problem behoben wurde.\n\nDer vollständige Systemneustart hat das Touchscreen-Problem behoben. Wenn Sie dieses Problem erneut haben, zögern Sie nicht, uns zu kontaktieren.\n\nGibt es noch etwas, bei dem ich Ihnen helfen kann?',
    'msg.touch_escalate': 'Es tut mir leid, dass das Problem weiterhin besteht. Ich werde einen Vorfall erstellen, damit sich ein spezialisierter Techniker so schnell wie möglich mit Ihnen in Verbindung setzt.\n\nBitte geben Sie Ihre Kontaktinformationen und alle zusätzlichen Details an, die Sie für relevant halten.',
    'msg.screen_off': 'Ich verstehe, dass der Bildschirm des Geräts ausgeschaltet ist.\n\nLassen Sie uns zuerst die Stromversorgung überprüfen:\nIst das POS (Zahlungsterminal) oder der Geldscheinprüfer eingeschaltet?',
    'opt.no_payment_off': 'Nein, Zahlungsmethoden sind ausgeschaltet',
    'opt.yes_payment_on': 'Ja, Zahlungsmethoden sind eingeschaltet',
    'opt.dont_know': 'Ich weiß nicht / Kann nicht überprüfen',
    'msg.power_check': 'Es scheint, dass das Gerät keine Stromversorgung erhält.\n\nBitte:\n1. Überprüfen Sie, ob das Stromkabel richtig angeschlossen ist\n2. Überprüfen Sie, ob die Steckdose Strom hat\n3. Schließen Sie das Gerät an die Stromversorgung an\n\nSchaltet sich das Gerät jetzt ein?',
    'opt.no_not_turn_on': 'Nein, das Gerät schaltet sich nicht ein',
    'opt.yes_works_now': 'Ja, das Gerät schaltet sich korrekt ein',
    'msg.power_escalate': 'Es gibt ein Problem mit dem elektrischen System des Geräts, das spezialisierte Aufmerksamkeit erfordert.\n\nIch werde einen dringenden Vorfall erstellen, damit sich ein Techniker so schnell wie möglich mit Ihnen in Verbindung setzt.',
    'msg.power_resolved': 'Perfekt! Das Gerät funktioniert jetzt korrekt.\n\nDas Problem war die elektrische Verbindung. Stellen Sie sicher, dass das Stromkabel gut befestigt ist, um versehentliche Trennungen zu vermeiden.\n\nBenötigen Sie Hilfe bei etwas anderem?',
    'msg.erp_check': 'Ich werde den Status des Geräts in unserem ERP-System überprüfen.\n\nBitte geben Sie die Geräte-ID oder Seriennummer an, um den Status (online/offline) zu überprüfen.',
    'opt.enter_machine_id': 'Geräte-ID eingeben',
    'msg.erp_status_check': 'ERP-Status wird überprüft...\n\n{ERP_STATUS_MESSAGE}\n\nBitte führen Sie Folgendes aus:\n1. Trennen Sie das Gerät von der Stromversorgung\n2. Warten Sie 10 Sekunden\n3. Schließen Sie es wieder an\n4. Beobachten Sie den blauen Initialisierungsbildschirm\n\nErscheint beim Start eine Meldung auf dem blauen Bildschirm?',
    'opt.no_message': 'Nein, es erscheint keine Meldung auf dem blauen Bildschirm',
    'opt.yes_message': 'Ja, es erscheint eine Meldung auf dem blauen Bildschirm',
    'opt.works_now': 'Das Gerät funktioniert jetzt korrekt',
    'msg.screen_hardware_escalate': 'Das Problem scheint Hardware-bezogen am Bildschirm zu sein. Es erfordert eine spezialisierte technische Überprüfung.\n\nIch werde einen Vorfall erstellen, damit sich ein Techniker mit Ihnen in Verbindung setzt.',
    'msg.screen_software_escalate': 'Das Problem scheint mit der Systemsoftware zusammenzuhängen. Es erfordert eine spezialisierte Diagnose.\n\nIch werde einen Vorfall erstellen, damit sich ein Techniker mit Ihnen in Verbindung setzt.',
    'msg.screen_resolved': 'Ausgezeichnet! Das Gerät funktioniert korrekt.\n\nDer Systemneustart hat das Problem behoben. Gibt es noch etwas, bei dem ich Ihnen helfen kann?',
    'msg.payment_issue': 'Welche Art von Zahlungsproblem haben Sie?',
    'opt.tpv_issue': 'POS funktioniert nicht',
    'opt.bill_acceptor': 'Geldscheinprüfer funktioniert nicht',
    'opt.card_issue': 'Akzeptiert keine Karten',
    'opt.bill_issue': 'Akzeptiert keine Geldscheine',
    'msg.tpv_issue': 'POS-Problem erkannt. Diese Art von Vorfall erfordert spezialisierte Aufmerksamkeit.\n\nIch werde ein Ticket erstellen, damit ein Techniker das Zahlungsterminal überprüft.',
    'msg.bill_acceptor_issue': 'Geldscheinprüfer-Problem erkannt. Diese Art von Vorfall erfordert spezialisierte Aufmerksamkeit.\n\nIch werde ein Ticket erstellen, damit ein Techniker den Geldscheinprüfer überprüft.',
    'msg.card_issue': 'Kartenleser-Problem erkannt. Diese Art von Vorfall erfordert spezialisierte Aufmerksamkeit.\n\nIch werde ein Ticket erstellen, damit ein Techniker das Kartenzahlungssystem überprüft.',
    'msg.bill_issue': 'Problem bei der Geldscheinannahme erkannt. Diese Art von Vorfall erfordert spezialisierte Aufmerksamkeit.\n\nIch werde ein Ticket erstellen, damit ein Techniker den Geldscheinprüfer überprüft.',
    'msg.other_issue': 'Bitte beschreiben Sie das Problem, das Sie haben, so detailliert wie möglich. Einschließlich:\n- Welche Komponente oder Funktionalität funktioniert nicht?\n- Wann hat das Problem begonnen?\n- Haben Sie Fehlermeldungen bemerkt?\n\nEin spezialisierter Techniker wird Ihren Fall überprüfen.',
    'opt.send_description': 'Beschreibung senden und Vorfall erstellen',
    'msg.other_escalate': 'Ich habe Ihren Fall registriert. Ein spezialisierter Techniker wird sich mit Ihnen in Verbindung setzen, um dieses Problem zu lösen.\n\nVielen Dank für die Bereitstellung der Details.',
    'opt.no_thanks': 'Nein, das ist alles. Danke',
    'opt.yes_other': 'Ja, ich habe ein anderes Problem',
    'msg.end': 'Vielen Dank, dass Sie den technischen Support von VACWAY kontaktiert haben. Wenn Sie in Zukunft Hilfe benötigen, zögern Sie nicht, eine neue Anfrage zu öffnen.\n\nSchönen Tag noch!'
  },
  it: {
    'msg.start': 'Ciao! Sono l\'assistente tecnico VACWAY. Sono qui per aiutarti con i problemi tecnici del chiosco.\n\nChe tipo di problema stai riscontrando?',
    'opt.touch_not_working': 'Touchscreen non funzionante',
    'opt.screen_off': 'Schermo spento',
    'opt.payment_issue': 'Problema metodo di pagamento',
    'opt.other_issue': 'Altro problema',
    'msg.touch_not_working': 'Capisco che il touchscreen non funziona.\n\nSi prega di seguire questo passaggio:\n1. Scollega completamente la macchina dalla corrente\n2. Attendi 10 secondi\n3. Ricollega la macchina\n\nIl touchscreen funziona dopo il riavvio?',
    'opt.yes_works': 'Sì, il touchscreen funziona ora',
    'opt.no_not_working': 'No, il touchscreen ancora non funziona',
    'msg.touch_resolved': 'Eccellente! Sono contento che il problema sia stato risolto.\n\nIl riavvio completo del sistema ha risolto il problema del touchscreen. Se riscontri nuovamente questo problema, non esitare a contattarci.\n\nC\'è qualcos\'altro in cui posso aiutarti?',
    'msg.touch_escalate': 'Mi dispiace che il problema persista. Creerò un incidente in modo che un tecnico specializzato ti contatti il prima possibile.\n\nFornisci le tue informazioni di contatto e qualsiasi dettaglio aggiuntivo che ritieni rilevante.',
    'msg.screen_off': 'Capisco che lo schermo della macchina è spento.\n\nPrima, controlliamo l\'alimentazione:\nIl POS (terminale di pagamento) o l\'accettatore di banconote sono accesi?',
    'opt.no_payment_off': 'No, i metodi di pagamento sono spenti',
    'opt.yes_payment_on': 'Sì, i metodi di pagamento sono accesi',
    'opt.dont_know': 'Non lo so / Non posso verificare',
    'msg.power_check': 'Sembra che la macchina non riceva alimentazione elettrica.\n\nPer favore:\n1. Verifica che il cavo di alimentazione sia collegato correttamente\n2. Controlla che la presa abbia corrente\n3. Collega la macchina all\'alimentazione\n\nLa macchina si accende ora?',
    'opt.no_not_turn_on': 'No, la macchina non si accende',
    'opt.yes_works_now': 'Sì, la macchina si accende correttamente',
    'msg.power_escalate': 'C\'è un problema con il sistema elettrico della macchina che richiede attenzione specializzata.\n\nCreerò un incidente urgente in modo che un tecnico ti contatti il prima possibile.',
    'msg.power_resolved': 'Perfetto! La macchina funziona correttamente ora.\n\nIl problema era la connessione elettrica. Assicurati che il cavo di alimentazione sia ben fissato per evitare disconnessioni accidentali.\n\nHai bisogno di aiuto per qualcos\'altro?',
    'msg.erp_check': 'Controllerò lo stato della macchina nel nostro sistema ERP.\n\nFornisci l\'ID o il numero di serie della macchina per verificare il suo stato (online/offline).',
    'opt.enter_machine_id': 'Inserisci ID macchina',
    'msg.erp_status_check': 'Controllo dello stato ERP...\n\n{ERP_STATUS_MESSAGE}\n\nPer favore, fai quanto segue:\n1. Scollega la macchina dalla corrente\n2. Attendi 10 secondi\n3. Ricollega\n4. Osserva lo schermo blu di inizializzazione\n\nAppare qualche messaggio sullo schermo blu all\'avvio?',
    'opt.no_message': 'No, non appare nessun messaggio sullo schermo blu',
    'opt.yes_message': 'Sì, appare un messaggio sullo schermo blu',
    'opt.works_now': 'La macchina funziona correttamente ora',
    'msg.screen_hardware_escalate': 'Il problema sembra essere hardware dello schermo. Richiede revisione tecnica specializzata.\n\nCreerò un incidente per un tecnico che ti contatterà.',
    'msg.screen_software_escalate': 'Il problema sembra essere correlato al software del sistema. Richiede diagnosi specializzata.\n\nCreerò un incidente per un tecnico che ti contatterà.',
    'msg.screen_resolved': 'Eccellente! La macchina funziona correttamente.\n\nIl riavvio del sistema ha risolto il problema. C\'è qualcos\'altro in cui posso aiutarti?',
    'msg.payment_issue': 'Che tipo di problema di pagamento stai riscontrando?',
    'opt.tpv_issue': 'POS non funzionante',
    'opt.bill_acceptor': 'Accettatore banconote non funzionante',
    'opt.card_issue': 'Non accetta carte',
    'opt.bill_issue': 'Non accetta banconote',
    'msg.tpv_issue': 'Problema POS rilevato. Questo tipo di incidente richiede attenzione specializzata.\n\nCreerò un ticket per un tecnico che riveda il terminale di pagamento.',
    'msg.bill_acceptor_issue': 'Problema accettatore banconote rilevato. Questo tipo di incidente richiede attenzione specializzata.\n\nCreerò un ticket per un tecnico che riveda l\'accettatore di banconote.',
    'msg.card_issue': 'Problema lettore carte rilevato. Questo tipo di incidente richiede attenzione specializzata.\n\nCreerò un ticket per un tecnico che riveda il sistema di pagamento con carta.',
    'msg.bill_issue': 'Problema accettazione banconote rilevato. Questo tipo di incidente richiede attenzione specializzata.\n\nCreerò un ticket per un tecnico che riveda l\'accettatore di banconote.',
    'msg.other_issue': 'Descrivi il problema che stai riscontrando nel modo più dettagliato possibile. Includi:\n- Quale componente o funzionalità non funziona?\n- Quando è iniziato il problema?\n- Hai notato messaggi di errore?\n\nUn tecnico specializzato rivedrà il tuo caso.',
    'opt.send_description': 'Invia descrizione e crea incidente',
    'msg.other_escalate': 'Ho registrato il tuo caso. Un tecnico specializzato ti contatterà per risolvere questo problema.\n\nGrazie per aver fornito i dettagli.',
    'opt.no_thanks': 'No, è tutto. Grazie',
    'opt.yes_other': 'Sì, ho un altro problema',
    'msg.end': 'Grazie per aver contattato il supporto tecnico VACWAY. Se hai bisogno di aiuto in futuro, non esitare ad aprire una nuova richiesta.\n\nBuona giornata!'
  },
  pt: {
    'msg.start': 'Olá! Sou o assistente técnico da VACWAY. Estou aqui para ajudá-lo com problemas técnicos do quiosque.\n\nQue tipo de problema você está enfrentando?',
    'opt.touch_not_working': 'Tela sensível ao toque não funciona',
    'opt.screen_off': 'Tela apagada',
    'opt.payment_issue': 'Problema com método de pagamento',
    'opt.other_issue': 'Outro problema',
    'msg.touch_not_working': 'Entendo que a tela sensível ao toque não está funcionando.\n\nPor favor, siga este passo:\n1. Desconecte completamente a máquina da corrente\n2. Aguarde 10 segundos\n3. Reconecte a máquina\n\nA tela sensível ao toque funciona após reiniciar?',
    'opt.yes_works': 'Sim, a tela sensível ao toque funciona agora',
    'opt.no_not_working': 'Não, a tela sensível ao toque ainda não funciona',
    'msg.touch_resolved': 'Excelente! Fico feliz que o problema tenha sido resolvido.\n\nO reinício completo do sistema resolveu o problema da tela sensível ao toque. Se você enfrentar este problema novamente, não hesite em nos contatar.\n\nHá mais alguma coisa em que eu possa ajudá-lo?',
    'msg.touch_escalate': 'Lamento que o problema persista. Vou criar um incidente para que um técnico especializado entre em contato com você o mais breve possível.\n\nPor favor, forneça suas informações de contato e quaisquer detalhes adicionais que considere relevantes.',
    'msg.screen_off': 'Entendo que a tela da máquina está apagada.\n\nPrimeiro, vamos verificar o fornecimento de energia:\nO POS (terminal de pagamento) ou o aceitador de notas estão ligados?',
    'opt.no_payment_off': 'Não, os métodos de pagamento estão desligados',
    'opt.yes_payment_on': 'Sim, os métodos de pagamento estão ligados',
    'opt.dont_know': 'Não sei / Não posso verificar',
    'msg.power_check': 'Parece que a máquina não está recebendo energia elétrica.\n\nPor favor:\n1. Verifique se o cabo de alimentação está conectado corretamente\n2. Verifique se a tomada tem energia\n3. Conecte a máquina à energia\n\nA máquina liga agora?',
    'opt.no_not_turn_on': 'Não, a máquina não liga',
    'opt.yes_works_now': 'Sim, a máquina liga corretamente',
    'msg.power_escalate': 'Há um problema com o sistema elétrico da máquina que requer atenção especializada.\n\nVou criar um incidente urgente para que um técnico entre em contato com você o mais breve possível.',
    'msg.power_resolved': 'Perfeito! A máquina está funcionando corretamente agora.\n\nO problema era a conexão elétrica. Certifique-se de que o cabo de alimentação esteja bem fixado para evitar desconexões acidentais.\n\nPrecisa de ajuda com mais alguma coisa?',
    'msg.erp_check': 'Vou verificar o status da máquina em nosso sistema ERP.\n\nPor favor, forneça o ID ou número de série da máquina para verificar seu status (online/offline).',
    'opt.enter_machine_id': 'Inserir ID da máquina',
    'msg.erp_status_check': 'Verificando status do ERP...\n\n{ERP_STATUS_MESSAGE}\n\nPor favor, faça o seguinte:\n1. Desconecte a máquina da corrente\n2. Aguarde 10 segundos\n3. Reconecte\n4. Observe a tela azul de inicialização\n\nAlguma mensagem aparece na tela azul durante a inicialização?',
    'opt.no_message': 'Não, nenhuma mensagem aparece na tela azul',
    'opt.yes_message': 'Sim, uma mensagem aparece na tela azul',
    'opt.works_now': 'A máquina funciona corretamente agora',
    'msg.screen_hardware_escalate': 'O problema parece ser de hardware da tela. Requer revisão técnica especializada.\n\nVou criar um incidente para que um técnico entre em contato com você.',
    'msg.screen_software_escalate': 'O problema parece estar relacionado ao software do sistema. Requer diagnóstico especializado.\n\nVou criar um incidente para que um técnico entre em contato com você.',
    'msg.screen_resolved': 'Excelente! A máquina está funcionando corretamente.\n\nO reinício do sistema resolveu o problema. Há mais alguma coisa em que eu possa ajudá-lo?',
    'msg.payment_issue': 'Que tipo de problema de pagamento você está enfrentando?',
    'opt.tpv_issue': 'POS não funciona',
    'opt.bill_acceptor': 'Aceitador de notas não funciona',
    'opt.card_issue': 'Não aceita cartões',
    'opt.bill_issue': 'Não aceita notas',
    'msg.tpv_issue': 'Problema de POS detectado. Este tipo de incidente requer atenção especializada.\n\nVou criar um ticket para que um técnico revise o terminal de pagamento.',
    'msg.bill_acceptor_issue': 'Problema de aceitador de notas detectado. Este tipo de incidente requer atenção especializada.\n\nVou criar um ticket para que um técnico revise o aceitador de notas.',
    'msg.card_issue': 'Problema de leitor de cartões detectado. Este tipo de incidente requer atenção especializada.\n\nVou criar um ticket para que um técnico revise o sistema de pagamento com cartão.',
    'msg.bill_issue': 'Problema de aceitação de notas detectado. Este tipo de incidente requer atenção especializada.\n\nVou criar um ticket para que um técnico revise o aceitador de notas.',
    'msg.other_issue': 'Por favor, descreva o problema que você está enfrentando com o máximo de detalhes possível. Inclua:\n- Qual componente ou funcionalidade não está funcionando?\n- Quando o problema começou?\n- Você notou alguma mensagem de erro?\n\nUm técnico especializado revisará seu caso.',
    'opt.send_description': 'Enviar descrição e criar incidente',
    'msg.other_escalate': 'Registrei seu caso. Um técnico especializado entrará em contato com você para resolver este problema.\n\nObrigado por fornecer os detalhes.',
    'opt.no_thanks': 'Não, isso é tudo. Obrigado',
    'opt.yes_other': 'Sim, tenho outro problema',
    'msg.end': 'Obrigado por entrar em contato com o suporte técnico da VACWAY. Se precisar de ajuda no futuro, não hesite em abrir uma nova consulta.\n\nTenha um ótimo dia!'
  }
};

// Add shorter versions for remaining languages (pl, tr, cs, sk, ca, nl, no)
translations.pl = { ...translations.en }; // Polish - using English as base
translations.tr = { ...translations.en }; // Turkish - using English as base  
translations.cs = { ...translations.en }; // Czech - using English as base
translations.sk = { ...translations.en }; // Slovak - using English as base
translations.ca = { ...translations.es }; // Catalan - using Spanish as base
translations.nl = { ...translations.en }; // Dutch - using English as base
translations.no = { ...translations.en }; // Norwegian - using English as base

/**
 * Complete decision tree for technical support
 */
export const decisionTree = {
  // Initial greeting
  start: new DecisionNode(
    'start',
    'msg.start',
    [
      { textKey: 'opt.touch_not_working', nextNode: 'touch_not_working' },
      { textKey: 'opt.screen_off', nextNode: 'screen_off' },
      { textKey: 'opt.payment_issue', nextNode: 'payment_issue' },
      { textKey: 'opt.other_issue', nextNode: 'other_issue' }
    ]
  ),

  // TOUCH SCREEN NOT WORKING FLOW
  touch_not_working: new DecisionNode(
    'touch_not_working',
    'msg.touch_not_working',
    [
      { textKey: 'opt.yes_works', nextNode: 'touch_resolved' },
      { textKey: 'opt.no_not_working', nextNode: 'touch_escalate' }
    ]
  ),

  touch_resolved: new DecisionNode(
    'touch_resolved',
    'msg.touch_resolved',
    [
      { textKey: 'opt.no_thanks', nextNode: 'end' },
      { textKey: 'opt.yes_other', nextNode: 'start' }
    ],
    false
  ),

  touch_escalate: new DecisionNode(
    'touch_escalate',
    'msg.touch_escalate',
    [],
    true,
    true
  ),

  // SCREEN OFF FLOW
  screen_off: new DecisionNode(
    'screen_off',
    'msg.screen_off',
    [
      { textKey: 'opt.no_payment_off', nextNode: 'power_check' },
      { textKey: 'opt.yes_payment_on', nextNode: 'erp_check' },
      { textKey: 'opt.dont_know', nextNode: 'power_check' }
    ]
  ),

  power_check: new DecisionNode(
    'power_check',
    'msg.power_check',
    [
      { textKey: 'opt.no_not_turn_on', nextNode: 'power_escalate' },
      { textKey: 'opt.yes_works_now', nextNode: 'power_resolved' }
    ]
  ),

  power_escalate: new DecisionNode(
    'power_escalate',
    'msg.power_escalate',
    [],
    true,
    true
  ),

  power_resolved: new DecisionNode(
    'power_resolved',
    'msg.power_resolved',
    [
      { textKey: 'opt.no_thanks', nextNode: 'end' },
      { textKey: 'opt.yes_other', nextNode: 'start' }
    ],
    false
  ),

  erp_check: new DecisionNode(
    'erp_check',
    'msg.erp_check',
    [
      { textKey: 'opt.enter_machine_id', nextNode: 'erp_status_check', requiresInput: true }
    ]
  ),

  erp_status_check: new DecisionNode(
    'erp_status_check',
    'msg.erp_status_check',
    [
      { textKey: 'opt.no_message', nextNode: 'screen_hardware_escalate' },
      { textKey: 'opt.yes_message', nextNode: 'screen_software_escalate' },
      { textKey: 'opt.works_now', nextNode: 'screen_resolved' }
    ]
  ),

  screen_hardware_escalate: new DecisionNode(
    'screen_hardware_escalate',
    'msg.screen_hardware_escalate',
    [],
    true,
    true
  ),

  screen_software_escalate: new DecisionNode(
    'screen_software_escalate',
    'msg.screen_software_escalate',
    [],
    true,
    true
  ),

  screen_resolved: new DecisionNode(
    'screen_resolved',
    'msg.screen_resolved',
    [
      { textKey: 'opt.no_thanks', nextNode: 'end' },
      { textKey: 'opt.yes_other', nextNode: 'start' }
    ],
    false
  ),

  // PAYMENT ISSUE FLOW
  payment_issue: new DecisionNode(
    'payment_issue',
    'msg.payment_issue',
    [
      { textKey: 'opt.tpv_issue', nextNode: 'tpv_issue' },
      { textKey: 'opt.bill_acceptor', nextNode: 'bill_acceptor_issue' },
      { textKey: 'opt.card_issue', nextNode: 'card_issue' },
      { textKey: 'opt.bill_issue', nextNode: 'bill_issue' }
    ]
  ),

  tpv_issue: new DecisionNode(
    'tpv_issue',
    'msg.tpv_issue',
    [],
    true,
    true
  ),

  bill_acceptor_issue: new DecisionNode(
    'bill_acceptor_issue',
    'msg.bill_acceptor_issue',
    [],
    true,
    true
  ),

  card_issue: new DecisionNode(
    'card_issue',
    'msg.card_issue',
    [],
    true,
    true
  ),

  bill_issue: new DecisionNode(
    'bill_issue',
    'msg.bill_issue',
    [],
    true,
    true
  ),

  // OTHER ISSUE FLOW
  other_issue: new DecisionNode(
    'other_issue',
    'msg.other_issue',
    [
      { textKey: 'opt.send_description', nextNode: 'other_escalate', requiresInput: true }
    ]
  ),

  other_escalate: new DecisionNode(
    'other_escalate',
    'msg.other_escalate',
    [],
    true,
    true
  ),

  // END NODE
  end: new DecisionNode(
    'end',
    'msg.end',
    [],
    true,
    false
  )
};

/**
 * Get current node from decision tree
 */
export const getNode = (nodeId, lang = null) => {
  const currentLang = lang || getCurrentLanguage();
  const node = decisionTree[nodeId] || decisionTree.start;
  
  // Return node with translated content
  return {
    ...node,
    message: node.getMessage(currentLang),
    options: node.getOptions(currentLang)
  };
};

/**
 * Process user selection and return next node
 */
export const processUserChoice = (currentNodeId, choiceIndex, userInput = null, lang = null) => {
  const currentLang = lang || getCurrentLanguage();
  const currentNode = getNode(currentNodeId, currentLang);
  
  if (!currentNode.options[choiceIndex]) {
    return null;
  }

  const selectedOption = currentNode.options[choiceIndex];
  
  return {
    nextNodeId: selectedOption.nextNode,
    userResponse: selectedOption.text,
    userInput: userInput
  };
};

/**
 * Check if current node requires escalation to incident
 */
export const shouldEscalate = (nodeId) => {
  const node = decisionTree[nodeId];
  return node?.escalate || false;
};

/**
 * Get conversation summary for incident creation
 */
export const getConversationSummary = (conversationHistory) => {
  return conversationHistory
    .map((item, index) => {
      if (item.isBot) {
        return `Soporte: ${item.message}`;
      } else {
        return `Cliente: ${item.message}`;
      }
    })
    .join('\n\n');
};
